import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  ChefHat, Clock, Check, X, Truck, Store, Loader2, Maximize2, Minimize2,
  ArrowLeft, Bell, UtensilsCrossed, StickyNote, Phone,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const ACTIVE = ['pending', 'confirmed', 'ready'];
const STATUS_LABEL = { pending: 'Jauns', confirmed: 'Gatavo', ready: 'Gatavs' };

// Wait-time colour thresholds (minutes): green < 5, amber 5–10, red > 10.
function waitTone(mins) {
  if (mins < 5) return { text: 'text-emerald-300', bg: 'bg-emerald-500/15', border: 'border-emerald-500/40', dot: '#34d399' };
  if (mins < 10) return { text: 'text-amber-300', bg: 'bg-amber-500/15', border: 'border-amber-500/50', dot: '#f59e0b' };
  return { text: 'text-red-300', bg: 'bg-red-500/20', border: 'border-red-500/60', dot: '#f87171' };
}

function fmtElapsed(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

function OrderTicket({ b, now, onAdvance, busyId }) {
  const createdMs = b.created_at ? new Date(b.created_at).getTime() : now;
  const elapsedMs = now - createdMs;
  const mins = elapsedMs / 60000;
  const tone = waitTone(mins);
  const lines = (b.cart && b.cart.length) ? b.cart : null;
  const busy = busyId === b.id;

  const next = b.status === 'pending'
    ? { to: 'confirmed', label: 'Apstiprināt', cls: 'bg-emerald-500 hover:bg-emerald-400 text-white' }
    : b.status === 'confirmed'
    ? { to: 'ready', label: 'Gatavs', cls: 'bg-amber-400 hover:bg-amber-300 text-black' }
    : { to: 'completed', label: 'Izsniegts', cls: 'bg-sky-500 hover:bg-sky-400 text-white' };

  return (
    <div className={`rounded-2xl border-2 ${tone.border} bg-[#141416] flex flex-col overflow-hidden`} data-testid={`kitchen-ticket-${b.id}`}>
      {/* Header: code + wait timer */}
      <div className="flex items-start justify-between gap-3 px-4 pt-4">
        <div className="min-w-0">
          <p className="font-mono text-3xl font-black leading-none text-white" data-testid={`kitchen-code-${b.id}`}>{b.code || '—'}</p>
          <p className="text-white/50 text-sm mt-1 truncate">{b.name || 'Klients'}</p>
        </div>
        <div className={`flex items-center gap-1.5 shrink-0 px-3 py-1.5 rounded-xl ${tone.bg} ${tone.text}`} data-testid={`kitchen-timer-${b.id}`}>
          <Clock className="w-4 h-4" />
          <span className="font-mono text-xl font-bold tabular-nums">{fmtElapsed(elapsedMs)}</span>
        </div>
      </div>

      {/* Fulfillment + time + status */}
      <div className="px-4 mt-3 flex items-center gap-2 flex-wrap text-sm">
        <span className="inline-flex items-center gap-1.5 text-white/80 font-semibold">
          {b.fulfillment === 'delivery' ? <Truck className="w-4 h-4 text-amber-400" /> : <Store className="w-4 h-4 text-amber-400" />}
          {b.fulfillment === 'delivery' ? 'Piegāde' : 'Uz vietas'}
        </span>
        {b.requested_time && <span className="text-white/50">· {b.requested_time}</span>}
        <span className={`ml-auto text-[11px] font-bold uppercase tracking-wide px-2 py-1 rounded-full ${tone.bg} ${tone.text}`}>{STATUS_LABEL[b.status] || b.status}</span>
      </div>

      {b.phone && (
        <div className="px-4 mt-1 flex items-center gap-1.5 text-white/40 text-xs"><Phone className="w-3.5 h-3.5" /> {b.phone}</div>
      )}
      {b.address && (
        <div className="px-4 mt-1 text-white/40 text-xs truncate">📍 {b.address}</div>
      )}

      {/* Items */}
      <div className="px-4 my-3 flex-1 space-y-2.5">
        {lines ? lines.map((l, i) => (
          <div key={i} className="flex gap-2" data-testid={`kitchen-item-${b.id}-${i}`}>
            <span className="text-amber-400 font-black text-lg leading-tight shrink-0">{l.qty}×</span>
            <div className="min-w-0">
              <p className="text-white font-semibold text-lg leading-tight">{l.name}</p>
              {l.options?.length > 0 && <p className="text-white/50 text-sm">{l.options.join(', ')}</p>}
              {l.note && <p className="text-amber-200/80 text-sm italic">↳ {l.note}</p>}
            </div>
          </div>
        )) : (
          <div className="flex gap-2">
            <span className="text-amber-400 font-black text-lg shrink-0">{b.quantity || 1}×</span>
            <p className="text-white font-semibold text-lg">{b.service_name || 'Pasūtījums'}</p>
          </div>
        )}
        {b.note && (
          <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20 px-2.5 py-1.5">
            <StickyNote className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
            <p className="text-amber-100 text-sm">{b.note}</p>
          </div>
        )}
      </div>

      {/* Footer: total + actions */}
      <div className="px-4 pb-4 pt-3 border-t border-white/10">
        {b.amount != null && (
          <div className="flex justify-between items-center mb-3 text-sm">
            <span className="text-white/40">{b.pay_choice === 'external' ? 'Maksā saņemot' : (b.payment_status === 'paid' ? 'Apmaksāts' : '')}</span>
            <span className="text-white font-bold text-lg">€{Number(b.amount || 0).toFixed(2)}</span>
          </div>
        )}
        <div className="flex gap-2">
          {b.status === 'pending' && (
            <button onClick={() => onAdvance(b.id, 'rejected')} disabled={busy}
              className="h-12 px-3 rounded-xl bg-white/10 text-white/70 hover:bg-white/20 disabled:opacity-50 flex items-center justify-center" data-testid={`kitchen-reject-${b.id}`}>
              <X className="w-5 h-5" />
            </button>
          )}
          <button onClick={() => onAdvance(b.id, next.to)} disabled={busy}
            className={`flex-1 h-12 rounded-xl font-bold text-base flex items-center justify-center gap-2 transition-colors disabled:opacity-60 ${next.cls}`}
            data-testid={`kitchen-advance-${b.id}`}>
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />} {next.label}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function KitchenScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pages, setPages] = useState([]);
  const [pageId, setPageId] = useState(() => new URLSearchParams(window.location.search).get('page') || '');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [busyId, setBusyId] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const audioCtxRef = useRef(null);
  const seenRef = useRef(null);

  const page = pages.find((p) => p.id === pageId);

  useEffect(() => { if (!user) navigate('/welcome'); }, [user, navigate]);

  // Load the owner's pages; pick a sensible default page if none in the URL.
  useEffect(() => {
    axios.get(`${API}/business-page/me/pages`).then(({ data }) => {
      const ps = data.pages || [];
      setPages(ps);
      if (!pageId && ps.length) {
        const menu = ps.find((p) => p.template === 'menu');
        setPageId((menu || ps[0]).id);
      }
    }).catch(() => {});
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Unlock Web Audio on first interaction (autoplay policy).
  useEffect(() => {
    const unlock = () => {
      try {
        if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();
      } catch (e) { /* no audio */ }
    };
    window.addEventListener('pointerdown', unlock);
    return () => window.removeEventListener('pointerdown', unlock);
  }, []);

  const playChime = useCallback(() => {
    const ctx = audioCtxRef.current;
    if (!ctx || !soundOn) return;
    try {
      const t0 = ctx.currentTime;
      [880, 1175].forEach((freq, i) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = 'sine'; o.frequency.value = freq;
        o.connect(g); g.connect(ctx.destination);
        const t = t0 + i * 0.18;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.3, t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.24);
        o.start(t); o.stop(t + 0.26);
      });
    } catch (e) { /* ignore */ }
  }, [soundOn]);

  const load = useCallback(async () => {
    if (!pageId) return;
    try {
      const { data } = await axios.get(`${API}/business-page/bookings/my`, { params: { page_id: pageId } });
      const active = (data.bookings || [])
        .filter((b) => b.kind === 'order' && ACTIVE.includes(b.status))
        .sort((a, c) => new Date(a.created_at || 0) - new Date(c.created_at || 0));
      // Chime when a brand-new pending order appears.
      const pendingIds = new Set(active.filter((b) => b.status === 'pending').map((b) => b.id));
      if (seenRef.current === null) {
        seenRef.current = pendingIds;
      } else {
        const fresh = [...pendingIds].filter((id) => !seenRef.current.has(id));
        seenRef.current = pendingIds;
        if (fresh.length) { playChime(); toast.success(`🔔 Jauns pasūtījums! (${active.length} aktīvi)`); }
      }
      setOrders(active);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, [pageId, playChime]);

  // Re-baseline the chime detector when switching pages.
  useEffect(() => { seenRef.current = null; setLoading(true); }, [pageId]);

  useEffect(() => {
    if (!pageId) return undefined;
    load();
    const iv = setInterval(load, 12000);
    return () => clearInterval(iv);
  }, [pageId, load]);

  // 1-second tick drives the live wait timers.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const advance = async (id, to) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/business-page/bookings/${id}/status`, { status: to });
      await load();
    } catch (e) { toast.error('Neizdevās atjaunināt'); }
    finally { setBusyId(null); }
  };

  const toggleFullscreen = () => {
    try {
      if (!document.fullscreenElement) { document.documentElement.requestFullscreen(); setFullscreen(true); }
      else { document.exitFullscreen(); setFullscreen(false); }
    } catch (e) { /* ignore */ }
  };

  const clock = new Date(now).toLocaleTimeString('lv-LV', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white flex flex-col" data-testid="kitchen-screen">
      {/* Header bar */}
      <header className="sticky top-0 z-10 bg-[#0A0A0B]/90 backdrop-blur border-b border-white/10 px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap">
        <button onClick={() => navigate('/business-templates?section=orders')} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center shrink-0" data-testid="kitchen-back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <ChefHat className="w-6 h-6 text-amber-400" />
          <h1 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Virtuves ekrāns</h1>
        </div>
        {pages.length > 1 && (
          <select value={pageId} onChange={(e) => setPageId(e.target.value)}
            className="bg-white/5 border border-white/15 rounded-lg px-3 py-1.5 text-sm text-white [color-scheme:dark]" data-testid="kitchen-page-select">
            {pages.map((p) => <option key={p.id} value={p.id} className="bg-[#0F0F0F]">{p.display_name || 'Lapa'}</option>)}
          </select>
        )}
        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <span className="hidden sm:inline text-white/40 text-sm font-mono">{clock}</span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/15 text-amber-300 text-sm font-bold" data-testid="kitchen-active-count">
            <UtensilsCrossed className="w-4 h-4" /> {orders.length} aktīvi
          </span>
          <button onClick={() => setSoundOn((s) => !s)} title={soundOn ? 'Izslēgt skaņu' : 'Ieslēgt skaņu'}
            className={`w-10 h-10 rounded-full flex items-center justify-center ${soundOn ? 'bg-amber-500/20 text-amber-300' : 'bg-white/10 text-white/40'}`} data-testid="kitchen-sound-toggle">
            <Bell className="w-5 h-5" />
          </button>
          <button onClick={toggleFullscreen} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" data-testid="kitchen-fullscreen">
            {fullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Tickets grid */}
      <main className="flex-1 p-4 sm:p-6">
        {loading ? (
          <div className="flex items-center justify-center gap-2 text-white/50 mt-24" data-testid="kitchen-loading"><Loader2 className="w-6 h-6 animate-spin" /> Ielādē…</div>
        ) : orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center mt-24" data-testid="kitchen-empty">
            <ChefHat className="w-16 h-16 text-white/15 mb-4" />
            <p className="text-2xl font-semibold text-white/60">Nav aktīvu pasūtījumu</p>
            <p className="text-white/35 mt-1">Jaunie pasūtījumi parādīsies šeit automātiski.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4" data-testid="kitchen-grid">
            {orders.map((b) => (
              <OrderTicket key={b.id} b={b} now={now} onAdvance={advance} busyId={busyId} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
